/* External */
import {
  memo,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import { useGtmLocalContext } from '~/providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '~/utils/googleTags';
import FocusableButton from '../../../components/FocusableButton';
import FocusableLink from '../../../components/FocusableLink';

/* Utils */
import { noOp } from '../../../utils/functions';
import { redirectTo } from '../../../utils/history';
import { getLink, Routes } from '../../../utils/router';
import { PRIVACY_POLICY_PAGE, TERMS_AND_CONDITIONS_PAGE } from '../../../components/TermsOfService/constants';
import { useOnBackPress } from '../../../utils/keyDownEvents';
import useOnDownArrowPress from '../../../hooks/useOnDownArrowPress';
import { Direction } from '../../../utils/spatialNavigation';

/* Providers */
import { useTermsOfServiceInfo } from '../../../providers/TermsOfServiceProvider';
import { useLoading } from '../../../providers/LoadingProvider';

/* Styles */
import './AcceptTermsOfService.scss';

function AcceptTermsOfService({ setFocus, realFocusKey }) {
  const acceptTermsOfServiceFocusKey = `${realFocusKey}-accept-terms-of-service`;

  const [lastButtonKeyFocused, setLastKeyButtonFocused] = useState(undefined);

  const { hideLoading, showLoading } = useLoading();
  const history = useHistory();

  const { acceptNewTerms, shouldAccept, currentPath } = useTermsOfServiceInfo();

  const shouldRedirectToPlayer = currentPath.toString().includes('player');

  const { commonTaggingEvent } = useGtmLocalContext();

  useOnBackPress({ listener: noOp });

  useEffect(() => {
    if (!lastButtonKeyFocused) {
      setLastKeyButtonFocused(acceptTermsOfServiceFocusKey);
      setFocus(acceptTermsOfServiceFocusKey);
    }
  }, [acceptTermsOfServiceFocusKey, lastButtonKeyFocused, hideLoading, setFocus]);

  useEffect(() => {
    if (!shouldAccept && !shouldRedirectToPlayer) {
      redirectTo(getLink(Routes.Private));
      hideLoading();
    }
  }, [shouldAccept, hideLoading, shouldRedirectToPlayer]);

  const handleOnAcceptToS = useCallback(async () => {
    try {
      showLoading();
      await acceptNewTerms();

      commonTaggingEvent({
        category: GTM_CATEGORIES.TERMS_OF_SERVICE_DATA,
        subCategory: GTM_SUBCATEGORIES.PAGES.TERMS_OF_SERVICE_PAGE,
        eventName: GTM_EVENT_NAMES.TERMS_OF_SERVICE.ACCEPT,
      });

      if (shouldRedirectToPlayer) {
        const playerIndex = currentPath.indexOf('/private');
        history.replace(currentPath.slice(Math.max(0, playerIndex)));
      } else {
        history.replace(getLink(Routes.Private));
      }

      hideLoading();
    } catch (error) {
      console.error('AcceptTermsOfService>handleOnAcceptToS', error);
      redirectTo(
        getLink(Routes.Private.AcceptTermsOfService.Error),
        {
          pathToRedirect: currentPath,
          type: 'UNEXPECTED',
          source: 'MIDDLEWARE',
          externCode: error?.response?.status,
        },
        true,
      );
      hideLoading();
    }
  }, [
    showLoading,
    acceptNewTerms,
    commonTaggingEvent,
    currentPath,
    history,
    shouldRedirectToPlayer,
    hideLoading,
  ]);

  useEffect(() => {
    commonTaggingEvent({
      category: GTM_CATEGORIES.TERMS_OF_SERVICE_DATA,
      subCategory: GTM_SUBCATEGORIES.PAGES.TERMS_OF_SERVICE_PAGE,
      eventName: GTM_EVENT_NAMES.TERMS_OF_SERVICE.PAGE,
    });
  }, [commonTaggingEvent]);

  const handleRedirectLink = useCallback(
    (page) => {
      redirectTo(getLink(Routes.Private.TermsOfService, {
        page,
      }));
    },
    [],
  );

  const redirectToTermsAndConditions = useCallback(
    () => {
      handleRedirectLink(TERMS_AND_CONDITIONS_PAGE);
    },
    [handleRedirectLink],
  );

  const redirectToPrivacyPolicy = useCallback(
    () => {
      handleRedirectLink(PRIVACY_POLICY_PAGE);
    },
    [handleRedirectLink],
  );

  const handleDownArrowPress = useOnDownArrowPress(() => {
    setFocus(lastButtonKeyFocused);
  });

  const handleUpArrowPress = (focusKey) => () => ({
    [Direction.UP]: setLastKeyButtonFocused(focusKey),
  });

  return (
    <div className="dtv-html5-c-accept-terms-of-service">
      <div className="dtv-html5-c-accept-terms-of-service__content">
        <h1 className="dtv-html5-c-accept-terms-of-service__title">
          <FormattedMessage id="termsOfService.title" />
        </h1>
        <p className="dtv-html5-c-accept-terms-of-service__subtitle">
          <FormattedMessage id="termsOfService.subtitle" />
        </p>
        <FocusableLink className="dtv-html5-c-accept-terms-of-service__link" onEnterPress={redirectToTermsAndConditions}>
          <FormattedMessage id="termsOfService.readTermsofConditions" />
        </FocusableLink>
        <FocusableLink
          className="dtv-html5-c-accept-terms-of-service__link"
          onEnterPress={redirectToPrivacyPolicy}
          onArrowPress={handleDownArrowPress}
        >
          <FormattedMessage id="termsOfService.readPrivacyPolicy" />
        </FocusableLink>
        <p className="dtv-html5-c-accept-terms-of-service__instructions">
          <FormattedMessage id="termsOfService.instructions" />
        </p>
        <div className="dtv-html5-c-accept-terms-of-service__content-buttons">
          <FocusableButton
            focusKey={acceptTermsOfServiceFocusKey}
            primary
            onEnterPress={handleOnAcceptToS}
            onArrowPress={handleUpArrowPress(acceptTermsOfServiceFocusKey)}
          >
            <FormattedMessage id="common.accept" />
          </FocusableButton>
        </div>
      </div>
    </div>
  );
}

AcceptTermsOfService.propTypes = {
  setFocus: PropTypes.func,
  realFocusKey: PropTypes.string,
};

AcceptTermsOfService.defaultProps = {
  setFocus: noOp,
  realFocusKey: '',
};

export default memo(withFocusable()(memo(AcceptTermsOfService)));
